// WebSocket protocol: JSON messages, discriminated on `t`. Shared. FROZEN CONTRACT.

import type {
  Building,
  BuildingType,
  FactionPublic,
  GameEvent,
  ItemType,
  Resource,
  Stockpile,
  StoreLine,
  SpillPile,
  UnitType,
  UnitWire,
} from './types'

// ---- Client -> Server ---------------------------------------------------------
import type { ExpeditionKind, Improvement, RealmView } from './realm'
import type { GovernorState, VillageFocus } from './governor'
import type { CampView } from './camp'
import type { FoodView } from './food'

export type ClientMsg =
  | { t: 'campProject'; active: boolean }
  | { t: 'villageWatch'; enabled: boolean }
  | { t: 'growth'; mode: 'steady' | 'paused' }
  | { t: 'governor'; enabled: boolean; focus: VillageFocus; takeOver?: boolean }
  | { t: 'staffing'; id: number; automatic: boolean }
  | { t: 'milestone'; id: string }
  | { t: 'improve'; id: Improvement }
  | { t: 'expedition'; kind: ExpeditionKind; ids: number[] }
  | { t: 'recallExpedition' }
  | { t: 'discovery'; serial: number; choice: 'keep' | 'salvage' }
  | { t: 'festival' }
  /** First message after connect. token resumes an existing player; otherwise a new one is created.
   *  build = the client bundle's BUILD_ID; a mismatch with the server prompts a refresh. */
  | { t: 'hello'; name: string; token?: string; build?: string }
  /** Set the kingdom (faction) name — used for the one-time "name your kingdom" prompt on first sign-in. */
  | { t: 'name'; name: string }
  /** Rename one of your own buildings ('b') or people ('u'). Empty name clears a building's custom name. */
  | { t: 'rename'; kind: 'b' | 'u'; id: number; name: string }
  /** Allow/forbid one of your serfs to scavenge the wilds when idle. */
  | { t: 'scav'; id: number; on: boolean }
  /** Empty start recruits one free citizen; empty stop recalls every explorer. */
  | { t: 'explore'; ids: number[]; on: boolean }
  /** Combat stance for one of your serfs: fight back when attacked, or always flee. */
  | { t: 'defend'; id: number; fight: boolean }
  /** Allow/forbid one of your armed serfs to patrol around their post when idle. */
  | { t: 'patrol'; id: number; on: boolean }
  /** Request map chunks by chunk key "cx,cy". Server replies with `chunk` msgs and an `ent` snapshot, and subscribes the client to updates in them. */
  | { t: 'chunks'; keys: string[] }
  /** Declare the client's currently-visible chunk keys. Cheap subscription refresh: the server makes the client's subscription set exactly these keys (no chunk payloads, no generation). */
  | { t: 'view'; keys: string[] }
  /** Place a building (or a road when type === 'road'). Server validates terrain, territory, cost.
   *  `dir` (DIRS index, oriented buildings only — the pier): the chosen facing; when absent
   *  or invalid the server picks one (pier: shoreline snap). Additive/optional. */
  | { t: 'build'; type: BuildingType; q: number; r: number; dir?: number }
  | { t: 'demolish'; id: number }
  | { t: 'train'; buildingId: number; unit: UnitType }
  /** Forge a gear item into the town-hall armory (Smithy). */
  | { t: 'forge'; buildingId: number; item: ItemType }
  /** Keep this many spare pieces of gear on the armory rack; combined minimums cannot exceed capacity. */
  | { t: 'armoryMinimum'; item: ItemType; min: number }
  /** Upgrade an owned hall; expected current phase rejects stale/double requests. */
  | { t: 'upgradeHall'; id: number; phase: import('./townhall').TownHallPhase }
  | { t: 'rally'; buildingId: number; q: number; r: number }
  /** Pause/resume a building: production & training stop, workers disperse, buffer still drains. */
  | { t: 'pause'; id: number; paused: boolean }
  /** Set a construction site's supply priority (clamped to −3…+3; 0 = normal). */
  | { t: 'prio'; id: number; prio: number }
  | { t: 'sprio'; id: number; prio: number }
  | { t: 'crew'; id: number; unit: number; add: boolean }
  | { t: 'store'; id: number; res: Resource; accept?: boolean; min?: number | null; max?: number | null }
  /** Command own units. action 'move': go to (q,r). action 'attack': attack entity or attack-move to (q,r). */
  | { t: 'cmd'; ids: number[]; action: 'move' | 'attack'; q?: number; r?: number; target?: { kind: 'u' | 'b'; id: number } }
  /** Put a real item from the armory into people's hands (item null ⇒ empty their hands).
   *  The item is reserved off the rack the moment the errand starts, so a batch never
   *  commits more people than there are items. */
  | { t: 'equip'; ids: number[]; item: ItemType | null }
  /** Cancel a pending gear-change errand for these people (returns any reserved item to the rack). */
  | { t: 'equipCancel'; ids: number[] }
  | { t: 'chat'; text: string }
  /** Propose an alliance, or accept a pending proposal from `to`. */
  | { t: 'ally'; to: string }
  /** Break an alliance (or withdraw/decline a proposal). */
  | { t: 'unally'; to: string }
  /** Rebuild a fresh village after total defeat (only valid when ruined). */
  | { t: 'respawn' }
  /** Start over: abandon the current realm (it decays away) and found a new village. */
  | { t: 'restart' }
  | { t: 'ping' }

// ---- Server -> Client -----------------------------------------------------------

/** One line in a resource's production/consumption breakdown. rate is per-minute,
 *  signed (+ produces, − consumes). idle = the source exists but is inactive. */
export interface FlowLine {
  label: string
  rate: number
  idle?: boolean
}

/** Per-resource economy breakdown for the hover tooltip. Rates are per-minute. */
export interface ResFlow {
  produce: number
  consume: number
  net: number
  lines: FlowLine[]
}

/** One kingdom's row on the public leaderboard. n = the ranked count. */
export interface LeaderRow {
  id: string
  name: string
  color: string
  n: number
}

export interface SelfInfo {
  camp?: CampView
  food?: FoodView
  governor?: GovernorState
  realm?: RealmView
  stockpile: Stockpile
  capacity: number
  pop: number
  popCap: number
  ruined: boolean
  /** Spare items in the armory (not currently in anyone's hands). */
  items: Partial<Record<ItemType, number>>
  armoryMinimums?: Partial<Record<ItemType, number>>
  /** Items already promised to people mid-swap (subtract from `items` for what's free to hand out). */
  reserved?: Partial<Record<ItemType, number>>
  /** Per-resource breakdown of which storage buildings hold it (notional fill, town hall first). */
  stores?: Partial<Record<Resource, StoreLine[]>>
  /** Per-resource production/consumption detail, keyed by Resource. */
  flows?: Record<Resource, ResFlow>
}

export type ServerMsg =
  | {
      t: 'welcome'
      you: string // your faction id
      token: string // persist in localStorage to log back in
      tick: number
      /** All factions known to the server (public views). */
      factions: FactionPublic[]
      self: SelfInfo
      home: { q: number; r: number }
      /** True on first sign-in before the player has named their kingdom — the client shows the naming prompt. */
      needsName?: boolean
      /** Admin spectator session: fog-free, read-only, no faction. */
      observer?: boolean
    }
  | {
      t: 'chunk'
      cx: number
      cy: number
      tiles: number[]
      roads: number[]
      owners: Record<string, string>
      /** Ground items in this chunk, keyed by local hex index. */
      drops?: Record<string, ItemType>
      /** Spilled resource piles in this chunk, keyed by local hex index. */
      spills?: Record<string, SpillPile>
      /** Per-tile height tiers, parallel to `tiles`. Present only when the
       *  elevation feature is enabled server-side; absent on flat worlds. */
      elev?: number[]
      /** Per-tile standing-tree flags (1 = a choppable tree), parallel to `tiles`.
       *  Absent on chunks with no woodland. */
      trees?: number[]
      /** True when this chunk was explored before this session: the client may
       *  render all of it as remembered terrain. Absent/false = explored just
       *  now — the client reveals it hex by hex as its vision sweeps. */
      mem?: boolean
    }
  /** Entity upserts/removals. b/u are full snapshots of changed entities; rb/ru are removed ids. */
  | { t: 'ent'; b?: Building[]; u?: UnitWire[]; rb?: number[]; ru?: number[] }
  /** Sent every server tick. */
  | { t: 'tick'; tick: number }
  /** Your own private stats changed. */
  | { t: 'self'; self: SelfInfo }
  /** A faction appeared or its public info changed (allies, online, ruined, name…). */
  | { t: 'faction'; f: FactionPublic }
  | { t: 'events'; list: GameEvent[] }
  | { t: 'chat'; from: string; name: string; text: string }
  /** Diplomacy state change involving you or anyone (for the player panel). */
  | { t: 'diplo'; from: string; to: string; state: 'proposed' | 'allied' | 'broken' | 'declined' }
  /** A single tile changed (road built, territory claimed/lost, item dropped/taken,
   *  goods spilled/collected). Omitted field = unchanged; owner '' = cleared;
   *  drop '' = cleared; spill '' = cleared. */
  | {
      t: 'tile'
      q: number
      r: number
      terrain?: number
      road?: boolean
      owner?: string
      drop?: ItemType | ''
      spill?: SpillPile | ''
      /** Height tier after a terraform (dig). Present on elevation worlds only. */
      elev?: number
      /** Standing-tree flag changed on this hex (true = a tree grew, false = it was
       *  felled/cleared). Omitted when unchanged. */
      tree?: boolean
    }
  /** Public standings: the top 3 kingdoms by territory hexes and by serf count.
   *  Broadcast periodically to every authenticated client and observer. */
  | { t: 'leaderboard'; hexes: LeaderRow[]; serfs: LeaderRow[] }
  | { t: 'error'; msg: string }
  /** The client is running an outdated bundle (or an operator forced it): show the
   *  refresh gate and reload. Sent on hello when build ids differ, or broadcast from admin. */
  | { t: 'reload'; reason?: string }
  | { t: 'pong' }

/** WebSocket close code: this socket was superseded by a newer session for the
 *  same account (another tab/device connected). The old client shows a "playing
 *  elsewhere" gate and does NOT reconnect. In the 4000–4999 app-defined range. */
export const WS_SUPERSEDED = 4002

export function encode(msg: ClientMsg | ServerMsg): string {
  return JSON.stringify(msg)
}

export function decode<T>(data: string): T | null {
  try {
    return JSON.parse(data) as T
  } catch {
    return null
  }
}
